import { PagedResultDto } from '@services';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 200;

/**
 * One build-wide list, loaded whole. Every one of these is read across the build's jobs, so a
 * page boundary would hide rows with nothing on screen to say so - the pages are walked until the
 * server's own count is met.
 *
 * Only while `enabled` (the drawer is open): these are opened rarely, and a build can hold a lot
 * of rows.
 */
export const useMasterBuildAggregate = <T>(
  enabled: boolean,
  fetchPage: (skipCount: number, maxResultCount: number) => Promise<PagedResultDto<T>>,
  deps: unknown[] = []
) => {
  const [dataSource, setDataSource] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const loaded: T[] = [];
        let totalCount = 0;
        do {
          // eslint-disable-next-line no-await-in-loop
          const page = await fetchPage(loaded.length, PAGE_SIZE);
          totalCount = page.totalCount;
          loaded.push(...page.items);
          // A page that comes back empty while the count says otherwise would loop forever.
          if (page.items.length === 0) break;
        } while (loaded.length < totalCount);
        if (!cancelled) setDataSource(loaded);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { loading, dataSource };
};
